/**
 * Counting how OKANE is used — counts, not people (docs/analytics-plan.md).
 * `count(name, label)` queues an event the list allows and refuses anything
 * else; the queue goes ten seconds after its first event, or when the page is
 * hidden or left. Nothing is kept on the device for it: a batch that cannot go
 * is dropped.
 *
 * It goes as a beacon — made for a page being left; in the browser suite a
 * `keepalive` fetch from `pagehide` lost the batch more often — to the page's *own* origin, `/api/usage`, which the web server passes to the API
 * (docker/nginx.conf). A beacon always carries the cookies of where it goes,
 * and this origin has none: the session cookie belongs to api.<host>.
 *
 * Only a built app sends (`import.meta.env.PROD`) — the dev server has no API —
 * and tests send where they say (`setUsageSender`).
 */
import { allowed } from '../lib/usage'
import { apiBase } from './cloud'

const AFTER_MS = 10_000
const MOST = 50 // what the server takes in one request

let queue = []
let timer = null
const refused = []

function post(events) {
  const url = `${location.origin}/api/usage`
  const body = JSON.stringify({ events })
  // text/plain keeps it a simple request: a beacon may not send anything else.
  if (navigator.sendBeacon?.(url, new Blob([body], { type: 'text/plain' }))) return
  try {
    fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'text/plain' },
      body,
      credentials: 'omit',
      keepalive: true,
    }).catch(() => {})
  } catch {
    // Dropped: nothing is kept to try again.
  }
}

let sender = import.meta.env?.PROD && typeof window !== 'undefined' ? post : null

/** Where batches go: a function of the events, or null to send nothing (the default outside a built app). */
export function setUsageSender(fn) {
  sender = fn
  queue = []
  clearTimeout(timer)
  timer = null
}

/** The events refused since the start — a test fails on any. */
export const usageRefused = () => [...refused]

/** Counts one event. Returns false — and counts nothing — for one the list does not allow. */
export function count(name, label = '') {
  const value = label == null ? '' : String(label)
  if (!allowed(name, value)) {
    refused.push({ name, label: value })
    return false
  }
  if (!sender) return true
  queue.push({ name, label: value })
  if (queue.length >= MOST) flushUsage()
  else if (!timer) timer = setTimeout(flushUsage, AFTER_MS)
  return true
}

/** Sends what is queued, now. */
export function flushUsage() {
  clearTimeout(timer)
  timer = null
  const events = queue
  queue = []
  for (let i = 0; i < events.length; i += MOST) sender?.(events.slice(i, i + MOST))
}

/** Sends the queue whenever the page is hidden or left — a reload included. */
export function startUsage(target = globalThis) {
  if (!target?.addEventListener) return
  target.addEventListener('pagehide', flushUsage)
  target.document?.addEventListener('visibilitychange', () => {
    if (target.document.visibilityState === 'hidden') flushUsage()
  })
}

/**
 * What has been counted, for an admin (`GET /api/usage` on the API, with the
 * session): `{ from, to, counts, visitors }`. Throws with the server's `code` —
 * `denied` for anyone else.
 */
export async function usageReport(from, to) {
  let res
  try {
    res = await fetch(`${apiBase()}/api/usage?${new URLSearchParams({ from, to })}`, { credentials: 'include' })
  } catch {
    throw Object.assign(new Error('The server cannot be reached.'), { code: 'offline' })
  }
  const data = await res.json().catch(() => null)
  if (!res.ok) throw Object.assign(new Error(data?.error?.message ?? res.statusText), { code: data?.error?.code ?? 'server' })
  return data
}
